import { Component, computed, inject, input, output, signal } from "@angular/core"
import { FirestoreReadService } from "../content/firestore-read.service"
import { faTag } from "@fortawesome/free-solid-svg-icons"
import { ShareYourStoryComponent } from "../shared/share-your-story.component"
import { Story } from "redlineLibs"
import { FileNamePipe } from "@shared"
import { StoryLinkedStoryComponent } from "./story-linked-story.component"
import { UiDialogComponent } from "ui-dialog"
import { UiIconComponent } from "ui-icon"
import { UiInheritDirective } from "ui-inherit"
import { SectionEnum } from "redlineLibs"
import { VideoViewComponent } from "../shared/fields/video/video-view.component"
import { StorageService } from "../../services/storage.service"
import { RouteService } from "../../services/route.service"

@Component({
  imports: [
    FileNamePipe,
    ShareYourStoryComponent,
    UiInheritDirective,
    UiIconComponent,
    StoryLinkedStoryComponent,
    UiDialogComponent,
    VideoViewComponent,
  ],
  standalone: true,
  template: `
    @if (!!story()) {

      <lib-ui-dialog (cancelAndClose)="cancelAndClose.emit()">
        <div ngProjectAs="title">
          <div
            class="line-clamp-1"
            style="font-size: 24px; font-weight: 600"
            [style.cursor]="showSelectedStory() ? 'pointer' : ''"
          >
            {{ title() }}
          </div>
        </div>

        <div ngProjectAs="content">
          <div
            class="row p-5 gap-8"
            style="align-items: flex-start"
          >

            <div
              class="column flex-1"
              style="min-width: 300px"
            >
              @if (title()) {
                <div class="center">
                  {{ title() }}
                </div>
              }
              @if (!videoView().src) {
                @if (imageView().src) {
                  <div>
                    <img [src]="imageView().src | sanitize: 'resourceUrl'" [alt]="imageView().alt"/>
                  </div>
                }
              }
              @if (videoView().src) {
                <e2e-video-view [videoUrl]="videoView().src"/>
              }
              @if (subTitle()) {
                <div class="italic">
                  {{ subTitle() }}
                </div>
              }
              @if (body()) {
                <div
                  class="italic body"
                  [innerHtml]="body()"
                ></div>
              }
              @if (note()) {
                A Note from the Curator
                <div class="italic"
                  [innerHtml]="note()"
                ></div>
              }
              @if (userName()) {
                <div class="italic">
                  from
                  <span class="text-primary">
                    {{ userName() }}
                  </span>
                </div>
              }
            </div>

            <div
              class="column flex-1"
              style="min-width: 300px"
            >
              <div class="row text-xl font-black">
                <div class="row-center border-2 border-base-content rounded-full" style="width: 34px">
                  <lib-ui-icon [icon]="faTag" class="text-primary"/>
                </div>
                Stories from the Community
              </div>

              <e2e-share-your-story (createNewContent)="createNewContent.emit()"/>

              @if (linkedStories_array().length > 0) {
                <div>
                  These stories are connected to a piece of history in the UDTR Timeline. Click the title to learn more
                  about their historical context and see other related user submissions.
                </div>
                <div class="column gap-2">
                  @for (linkedStory of linkedStories_array(); track linkedStory.id) {
                    <e2e-story-linked-story
                      [story]="linkedStory"
                      (goToLinkedStory)="goToLinkedStory(linkedStory.id)"
                    />
                  }
                </div>
              }
            </div>
          </div>
        </div>
      </lib-ui-dialog>
    }
  `,
  selector: "e2e-story-details",
})
export class StoryDetailsComponent {
  private storageService = inject(StorageService)
  private firestoreReadService = inject(FirestoreReadService)
  private routeService = inject(RouteService)

  contentId = input.required<string>()
  linkedStoryContentId = signal<undefined | string>(undefined)

  contentIdDisplayed = computed(() =>
    this.linkedStoryContentId() || this.contentId(),
  )

  cancelAndClose = output()
  createNewContent = output<void>()

  path = computed(() => {
    return "polygon(0% 20%, 30% 0%, 70% 0%, 100% 20%, 100% 100%, 0% 100%)"
  })
  story = computed(() =>
    this.firestoreReadService.getStoryById(this.contentIdDisplayed()),
  ) // .getStoryById(this.routeService.timelineIdFromUrl()))
  title = computed(() => this.story()?.[SectionEnum.TITLE].value || "")
  subTitle = computed(() => this.story()?.[SectionEnum.SUBTITLE].value || "")
  body = computed(() => this.story()?.[SectionEnum.BODY].value || "")
  note = computed(() => this.story()?.[SectionEnum.NOTE].value || "")
  imageView = computed(() => {
    const image = this.story()?.[SectionEnum.IMAGE]
    return {
      alt: image?.alt || "",
      src: (image?.filePath && this.storageService.getFileUrl(image.filePath)) || "",
    }
  })
  videoView = computed(() => {
    const video = this.story()?.[SectionEnum.VIDEO]
    return {
      src: (video?.filePath && this.storageService.getFileUrl(video.filePath)) || "",
    }
  })
  linkedStories_array = computed(() => {
    const links_set = this.firestoreReadService.allContentLinks_map().get(this.contentIdDisplayed()) // .get(this.modalStory()?.id || "")
    return links_set &&
      Array
        .from(links_set.values())
        .map((contentId) => this.firestoreReadService.getStoryById(contentId))
        .filter((story): story is Story => Boolean(story))
      || [] as Story[]
  })
  userName = computed(() =>
    this.story()?.created.userName || "",
  )

  showSelectedStory = signal(false)

  goToLinkedStory(storyId: string) {
    this.linkedStoryContentId.set(storyId)
  }

  protected readonly faTag = faTag
}
